/**
 * @module DocumentoItem
 * @description Componente que renderiza un documento de pago individual dentro de la lista.
 * Cada documento muestra hasta 3 opciones de pago según la configuración del servicio:
 * - Pago mínimo: valor mínimo exigido para el documento
 * - Pago total: valor completo del documento (solo si es abonable y valorTotal > 0)
 * - Otro valor: campo de entrada libre para abono personalizado (solo si es abonable)
 *
 * Incluye indicadores visuales de selección (radio buttons), fechas de vencimiento
 * responsivas (versión desktop/mobile) y mensajes de bloqueo por transacciones en proceso.
 *
 * Los valores pre-computados (montos formateados, vencimiento, etc.) se reciben
 * como props desde DocumentoPago para separar lógica de cálculo de presentación.
 */

import { FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MoneyMask } from "../../../helpers";
import { Idocumento } from "../../../helpers/dataExamples";
import { IconButton, Tooltip } from "@mui/material";
import { usePagosOnline } from "../PagosOnlineContext";

/**
 * Props para DocumentoItem.
 * Combina datos crudos del documento con valores pre-computados y callbacks.
 */
interface DocumentoItemProps {
  /** Datos del documento de pago (referencia, valores, servicio, etc.) */
  doc: Idocumento;
  /** Índice del documento en la lista, usado como key de React */
  index: number;
  /** true si esta referencia es la actualmente seleccionada */
  checked: boolean;
  /** true si el tipo de servicio permite abonos parciales */
  abonable: boolean | undefined;
  /** Monto mínimo de abono (calculado como % del porcentajeAbono del servicio) */
  minimoAbono: number;
  /** Valor total formateado con separadores de miles */
  moneyPagoTotal: string;
  /** Valor mínimo formateado con separadores de miles */
  moneyPagoMinimo: string;
  /** Fecha de vencimiento formateada (YYYY-MM-DD o "No disp.") */
  vencimiento: string;
  /** Tipo de pago seleccionado para esta referencia: "min", "tot" o "otr" */
  tipoPagoRef: string;
  /** Callback invocado al seleccionar un radio button de esta referencia */
  onActivarReferencia: (doc: Idocumento, tipoRadio: string) => void;
  /** Callback invocado al cambiar el valor en el campo de abono personalizado */
  onRealizarAbono: (
    event: React.ChangeEvent<HTMLInputElement>,
    referencia: string
  ) => void;
}

const DocumentoItem: FC<DocumentoItemProps> = ({
  doc,
  index,
  checked,
  abonable,
  minimoAbono,
  moneyPagoTotal,
  moneyPagoMinimo,
  vencimiento,
  tipoPagoRef,
  onActivarReferencia,
  onRealizarAbono,
}) => {
  /** Accede al estado global para leer el valor de abono actual */
  const { estado } = usePagosOnline();

  return (
    <div
      className={
        "referencia-pago " +
        (checked ? "active " : " ") +
        (doc.trans_bloq ? "border-warning " : "")
      }
      key={index}
    >
      <label
        className={
          "d-flex flex-stack cursor-pointer rounded-2  " +
          (checked ? "active" : "")
        }
        id={"referencia-" + doc.referencia}
        key={index}
      >
        <span className="d-flex align-items-center me-2 w-100">
          {/* Ícono indicador de documento (cambia color si está seleccionado) */}
          <span className="symbol symbol-50px me-6 d-xs-none d-sm-none d-dm-block d-lg-block d-none">
            <span className="symbol-label bg-light-comfe-b">
              <i
                className={
                  "fab fa-html5  fs-2x " +
                  (checked ? "text-comfe" : "text-muted")
                }
              ></i>
              <FontAwesomeIcon
                className={
                  "fab fa-html5 fs-2x " +
                  (checked ? "text-comfe" : "text-muted")
                }
                icon={"dollar-sign"}
              />
            </span>
          </span>

          <div className="d-flex flex-column flex-fill">
            {/* Información de la referencia: número y descripción */}
            <span
              className={
                "fs-7 mb-2 " + (checked ? "" : "text-muted")
              }
            >
              Ref. {doc.referencia} - {doc.detalle}
            </span>
            <div className="row">
              {/* ════════ OPCIÓN: PAGO MÍNIMO ════════ */}
              <div
                className={
                  "row col-12 col-sm-12 col-md-12 col-lg-12 fs-7 text-start mb-2 " +
                  (checked ? "" : "text-muted ")
                }
              >
                <div className="col-2 col-sm-1 col-md-1 col-lg-1">
                  <span className="form-check form-check-custom form-check-solid">
                    <input
                      className={"form-check-input "}
                      type={"radio"}
                      value={"min"}
                      name={"tipo" + doc.referencia}
                      onChange={(a) =>
                        onActivarReferencia(doc, a.target.value)
                      }
                      checked={
                        tipoPagoRef === "min" &&
                        estado.referenciaActiva === doc.referencia
                      }
                      disabled={doc.valorMinimo === 0 || doc.trans_bloq}
                    />
                  </span>
                </div>
                <div className="col-10 col-sm-6 col-md-6 col-lg-6">
                  Pago mínimo: ${moneyPagoMinimo}
                </div>

                {/* Vencimiento: versión desktop (visible en md+) */}
                <div className="col-10 col-sm-5 col-md-5 col-lg-5 d-none d-md-block d-lg-block">
                  Vence: {vencimiento}
                </div>

                {/* Vencimiento: versión mobile (visible en xs/sm) */}
                <span
                  className={
                    "col-md-12 fs-7 text-start mb-2 offset-2 offset-sm-1 offset-md-1 offset-lg-1 d-lg-none d-md-none d-xs-block d-sm-block" +
                    (checked ? "" : "text-muted")
                  }
                >
                  Vence: {vencimiento}
                </span>
              </div>

              {/* ════════ OPCIÓN: PAGO TOTAL ════════ */}
              {/* Solo visible si el servicio es abonable y el valor total > 0 */}
              <div
                className={
                  "row col-12 col-sm-12 col-md-12 col-lg-12 fs-7 text-start mb-2" +
                  (checked ? "" : " text-muted") +
                  (abonable ? "" : " d-none ") +
                  (doc.valorTotal === 0 ? " d-none" : "")
                }
              >
                <div className="col-2 col-sm-1 col-md-1 col-lg-1">
                  <span className="form-check form-check-custom form-check-solid">
                    <input
                      className={"form-check-input"}
                      type={"radio"}
                      value={"tot"}
                      name={"tipo" + doc.referencia}
                      onChange={(a) =>
                        onActivarReferencia(doc, a.target.value)
                      }
                      checked={
                        tipoPagoRef === "tot" &&
                        estado.referenciaActiva === doc.referencia
                      }
                      disabled={doc.trans_bloq}
                    ></input>
                  </span>
                </div>
                <div className="col-10 col-sm-11 col-md-11 col-lg-11">
                  Pago total: ${moneyPagoTotal}
                </div>
              </div>

              {/* ════════ OPCIÓN: OTRO VALOR (abono personalizado) ════════ */}
              {/* Solo visible si el servicio permite abonos */}
              {abonable && (
                <div
                  className={
                    "row col-12 col-sm-12 col-md-12 col-lg-12 fs-7 text-start mb-2" +
                    (checked ? "" : " text-muted")
                  }
                >
                  <div
                    className={
                      "col-2 col-sm-1 col-md-1 col-lg-1 "
                    }
                  >
                    <span className="form-check form-check-custom form-check-solid">
                      <input
                        className={"form-check-input mt-1"}
                        type={"radio"}
                        value={"otr"}
                        name={"tipo" + doc.referencia}
                        onChange={(a) => {
                          onActivarReferencia(doc, a.target.value);
                        }}
                        checked={
                          tipoPagoRef === "otr" &&
                          estado.referenciaActiva === doc.referencia
                        }
                        disabled={doc.trans_bloq}
                      />
                    </span>
                    {/* Tooltip con rango válido de abono: [minimoAbono, valorTotal] */}
                    <Tooltip
                      className={
                        tipoPagoRef !== "otr" ? "d-none" : ""
                      }
                      title={
                        <h3 style={{ color: "white" }}>
                          Ingrese un valor comprendido entre{" "}
                          {MoneyMask(minimoAbono + "")} y{" "}
                          {MoneyMask(
                            (doc.valorTotal === 0
                              ? doc.valorMinimo
                              : doc.valorTotal) + ""
                          )}
                        </h3>
                      }
                    >
                      <IconButton>
                        <FontAwesomeIcon
                          icon={"question"}
                          style={{ fontSize: "0.75rem" }}
                        />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="col-10 col-sm-11 col-md-11 col-lg-11">
                    <span
                      className={
                        "mt-2 me-3 " +
                        (checked ? "" : "text-muted") +
                        (abonable ? " " : " d-none")
                      }
                    >
                      Otro valor:
                    </span>
                    {/* Input de abono libre: limitado entre minimoAbono y valorTotal */}
                    <input
                      type="text"
                      className={
                        "form-control form-control-sm rounded-pill col-md-4 mt-3"
                      }
                      id="input_abono"
                      maxLength={10}
                      placeholder="Ingrese el valor a pagar"
                      value={
                        checked
                          ? estado.referencias[doc.referencia]
                              .valor_abono
                          : ""
                      }
                      min={minimoAbono}
                      max={doc.valorTotal}
                      onChange={(event) =>
                        onRealizarAbono(event, doc.referencia)
                      }
                      disabled={
                        !checked ||
                        (tipoPagoRef !== "otr" &&
                          estado.referenciaActiva ===
                            doc.referencia) ||
                        doc.trans_bloq
                      }
                    ></input>
                  </div>
                </div>
              )}

              {/* ════════ MENSAJE DE BLOQUEO ════════ */}
              {/* Visible cuando la referencia tiene una transacción en proceso */}
              {doc.trans_bloq && (
                <p className="text-end text-warning2">
                  <i>
                    <FontAwesomeIcon icon={"exclamation-triangle"} />
                  </i>
                  Transacción en proceso!
                </p>
              )}
            </div>
          </div>
        </span>
      </label>
    </div>
  );
};

export { DocumentoItem };
